// hooks/tasks/useTasks.ts
import { useState, useEffect, useCallback } from "react";
import { Task, TaskCreatePayload, TaskUpdatePayload, TaskPriority, TaskStatus, Agent } from "../../types/Task";
import { taskService, TaskFilters, TaskStats } from "../../services/taskService";
import { PaginatedResponse } from "../../types/core/PaginatedResponse";
import { sortByDate } from "../../helpers/sortDataByDate";
import useToken from "../../../../hooks/auth/useToken";
import useCurrentUser from "../../../../hooks/auth/useCurrentUser";

export default function useTasks() {
  const { token } = useToken();
  const { currentUser: user } = useCurrentUser();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [admins, setAdmins] = useState<Agent[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // ============================================
  // 📋 LISTE & DÉTAIL
  // ============================================

  const fetchTasks = useCallback(
    async (page: number = 1, perPage: number = 20, filters?: TaskFilters) => {
      setLoading(true);
      setError(null);
      try {
        const response: PaginatedResponse<Task> = await taskService.getTasks(page, perPage, filters, token);

        const dataEnrichies = response.data;

        const sortedTasks = sortByDate(dataEnrichies, (task) => task.created_at);
        setTasks(sortedTasks);

        setPagination({
          currentPage: response.current_page,
          lastPage: response.last_page,
          total: response.total,
          perPage: response.per_page,
        });
      } catch (err) {
        setError("Erreur lors du chargement des tâches");
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const fetchTaskById = useCallback(
    async (id: string): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const task = await taskService.getTaskById(id, token);
        if (!task) setError("Tâche non trouvée");
        return task;
      } catch (err) {
        setError("Erreur lors du chargement de la tâche");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const fetchTasksByClient = useCallback(
    async (clientId: string, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Task>> => {
      setLoading(true);
      setError(null);
      try {
        return await taskService.getTasksByClient(clientId, page, perPage, token);
      } catch (err) {
        setError("Erreur lors du chargement des tâches du client");
        console.error(err);
        return {
          current_page: 1,
          data: [],
          first_page_url: "",
          from: null,
          last_page: 1,
          last_page_url: "",
          links: [],
          next_page_url: null,
          path: "",
          per_page: perPage,
          prev_page_url: null,
          to: null,
          total: 0,
        };
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // ============================================
  // ✏️ CRÉATION & MISE À JOUR
  // ============================================

  const createTask = useCallback(
    async (payload: TaskCreatePayload): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const newTask = await taskService.createTask(payload, token);
        setTasks((prev) => [newTask, ...prev]);
        return newTask;
      } catch (err) {
        setError("Erreur lors de la création de la tâche");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const updateTask = useCallback(
    async (id: string, data: TaskUpdatePayload): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.updateTask(id, data, token);
        setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors de la mise à jour de la tâche");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const updateStatus = useCallback(
    async (id: string, status: TaskStatus): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.updateStatus(id, status, token);
        setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors du changement de statut");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const updatePriority = useCallback(
    async (id: string, priority: TaskPriority): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.updatePriority(id, priority, token);
        setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors du changement de priorité");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // ============================================
  // 👥 ASSIGNATION (multi-admins)
  // ============================================

  const assignAgents = useCallback(
    async (taskId: string, agentIds: string[]): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.assignAgents(taskId, agentIds, token);
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors de l'assignation des admins");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const unassignAgent = useCallback(
    async (taskId: string, agentId: string): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.unassignAgent(taskId, agentId, token);
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors du retrait de l'admin");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // ============================================
  // 📎 PIÈCES JOINTES
  // ============================================

  const addAttachments = useCallback(
    async (taskId: string, attachments: string[]): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.addAttachments(taskId, attachments, token);
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors de l'ajout des pièces jointes");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const removeAttachment = useCallback(
    async (taskId: string, attachment: string): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.removeAttachment(taskId, attachment, token);
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors de la suppression de la pièce jointe");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // ============================================
  // ✅ RÉSOLUTION & SUPPRESSION
  // ============================================

  const resolveTask = useCallback(
    async (taskId: string): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.resolveTask(taskId, token);
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors de la résolution de la tâche");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const holdTask = useCallback(
    async (taskId: string): Promise<Task | null> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await taskService.holdTask(taskId, token);
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
        return updated;
      } catch (err) {
        setError("Erreur lors de la mise en attente");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const deleteTask = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await taskService.deleteTask(id, token);
        setTasks((prev) => prev.filter((t) => t.id !== id));
        return true;
      } catch (err) {
        setError("Erreur lors de la suppression de la tâche");
        console.error(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // ============================================
  // 📊 STATISTIQUES & COMPTEURS
  // ============================================

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await taskService.getStats(token, user?.id);
      setStats(data);
    } catch (err) {
      setError("Erreur lors du chargement des statistiques");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  const fetchMyPendingCount = useCallback(async (): Promise<number> => {
    try {
      return await taskService.getMyPendingCount(token, user?.id);
    } catch (err) {
      console.error(err);
      return 0;
    }
  }, [token, user?.id]);

  const fetchUnreadCount = useCallback(async (): Promise<number> => {
    try {
      return await taskService.getUnreadCount(token, user?.id);
    } catch (err) {
      console.error(err);
      return 0;
    }
  }, [token, user?.id]);

  // ============================================
  // 👤 ADMINS
  // ============================================

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await taskService.getAdmins(token);
      setAdmins(data);
    } catch (err) {
      setError("Erreur lors du chargement des admins");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ============================================
  // 🔄 CHARGEMENT INITIAL
  // ============================================

  useEffect(() => {
    fetchTasks();
    fetchStats();
    fetchAdmins();
  }, [fetchTasks, fetchStats, fetchAdmins]);

  return {
    // État
    tasks,
    loading,
    error,
    stats,
    admins,
    pagination,

    // Lecture
    fetchTasks,
    fetchTaskById,
    fetchTasksByClient,

    // Écriture
    createTask,
    updateTask,
    updateStatus,
    updatePriority,

    // Assignation
    assignAgents,
    unassignAgent,

    // Pièces jointes
    addAttachments,
    removeAttachment,

    // Cycle de vie
    resolveTask,
    holdTask,
    deleteTask,

    // Stats & compteurs
    fetchStats,
    fetchMyPendingCount,
    fetchUnreadCount,

    // Admins
    fetchAdmins,
  };
}
